import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Divider,
  FormLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useNotification } from '@/context/notification';
import { useEditEmployeeEmail } from '@/data/employees';
import type { Employee } from '@/data/employees/interfaces';
import { isValidEmail } from '@/utils/form-validators/email';

const emailValidationSchema = z.object({
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .refine((email: string) => isValidEmail(email), {
      message: 'Informe um email válido!',
    }),
});

const createInitialValues = (employee: Employee) => ({
  name: employee?.nome,
  email: employee?.email,
});

interface EditEmailDialogProps {
  employee: Employee;
  onClose: () => void;
}

export const EditEmailDialog = ({
  employee,
  onClose,
}: EditEmailDialogProps) => {
  const { toast } = useNotification();
  const { mutate: editEmployeeEmail, isPending } = useEditEmployeeEmail();
  const { control, handleSubmit } = useForm({
    defaultValues: createInitialValues(employee),
    resolver: zodResolver(emailValidationSchema),
  });

  const handleUpdate = async (data: z.infer<typeof emailValidationSchema>) => {
    editEmployeeEmail(
      { id: employee.id, email: data.email },
      {
        onSuccess: ({ data }) => {
          toast({
            content: data?.message || 'Email alterado com sucesso.',
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          toast({
            content:
              (error as AxiosError<{ data: { message: string } }>)?.response
                ?.data?.data?.message || 'Erro ao alterar email.',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <BaseDialog
      onClose={onClose}
      fullWidth
      open
      title="Alteração de email"
      actionsComponent={
        <>
          <Button onClick={onClose} variant="outlined">
            Cancelar
          </Button>
          <Button
            loading={isPending}
            onClick={handleSubmit(handleUpdate)}
            variant="contained"
          >
            Confirmar
          </Button>
        </>
      }
    >
      <form>
        <Stack spacing={2}>
          <Typography>
            O email é utilizado para o usuário entrar na plataforma. Após essa
            alteração, o usuário precisará usar o novo email para isso.
          </Typography>
        </Stack>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid size={12}>
            <FormLabel>Novo email*</FormLabel>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="nome@nomedaempresa.com.br"
                  variant="outlined"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </BaseDialog>
  );
};
